import {
  Box,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DialogConfirmDelete from "@saleor/components/DialogConfirmDelete";
import MyTypography from "@saleor/components/MyTypography";
import TableCellHeader from "@saleor/components/TableCellHeader";
import {
  PoUploadCustomerSettingsQuery,
  usePoUploadCustomerSettingsDeleteMutation,
  usePoUploadCustomerSettingsUpdateMutation
} from "@saleor/graphql";
import { IconButton } from "@saleor/macaw-ui";
import UISwitch from "@saleor/userManagement/components/UISwitch";
import React, { FC, useState } from "react";
import { useIntl } from "react-intl";

import POSettingsAddButton from "../POSettingsAddButton";

const useStyles = makeStyles(
  () => ({
    root: {
      borderRadius: "2px",
      overflowX: "auto",
      width: 700,
      "&::-webkit-scrollbar": {
        height: "14px",
        background: "#FFFFFF"
      },
      "&::-webkit-scrollbar-track": {
        borderRadius: 10
      },
      "&::-webkit-scrollbar-thumb": {
        background: "#CCCCCC",
        borderRadius: 10,
        border: "4px solid #FFFFFF",
        height: "6px"
      }
    },
    tableWrapper: {
      "& .MuiTableCell-head *": {
        color: "#222222 !important"
      }
    },
    rowBody: {
      height: 64
    },
    tableHeaderCell: {
      width: 300,
      paddingLeft: 12
    },
    title: {
      margin: "40px 0 24px 0"
    },
    colSwitch: {
      "&.MuiTableCell-head div": {
        justifyContent: "center"
      },
      "& .MuiFormGroup-root": {
        justifyContent: "center",
        flexDirection: "unset"
      }
    },
    colDelete: {
      "&.MuiTableCell-head div": {
        justifyContent: "center"
      },
      textAlign: "center",
      "& .MuiSvgIcon-root": {
        width: 20,
        height: 20
      }
    },
    flexBox: {
      width: 700,
      display: "flex",
      justifyContent: "space-between"
    }
  }),
  { name: "POSettingsTable" }
);

enum ModalMode {
  ADD = "ADD",
  DELETE = "DELETE"
}

interface POSettingsTableProps {
  data?: PoUploadCustomerSettingsQuery;
  refetch?: () => void;
}

const POSettingsTable: FC<POSettingsTableProps> = ({ data, refetch }) => {
  const intl = useIntl();
  const classes = useStyles();
  const [selectedSoldToId, setSelectedSoldToId] = useState(null);
  const [modalMode, setModalMode] = useState(null);
  const [updateMutation] = usePoUploadCustomerSettingsUpdateMutation({
    onCompleted: refetch
  });
  const [deleteMutation] = usePoUploadCustomerSettingsDeleteMutation({
    onCompleted: () => {
      setModalMode(null);
      refetch();
    }
  });

  const handleToggleSwitch = (soldToId, e) => {
    updateMutation({
      variables: {
        soldToId,
        useCustomerMaster: e.target.value
      }
    });
  };
  const handleDelete = soldToId => {
    deleteMutation({
      variables: {
        soldToId
      }
    });
  };

  return (
    <Box className={classes.tableWrapper}>
      <div className={classes.flexBox}>
        <MyTypography className={classes.title} variant="h7">
          {intl.formatMessage({ defaultMessage: "PO Master Mapping" })}
        </MyTypography>
        <POSettingsAddButton refetch={refetch} />
      </div>
      <div className={classes.root}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader>Customer</TableCellHeader>
              <TableCellHeader className={classes.colSwitch}>
                Customer Master
              </TableCellHeader>
              <TableCellHeader className={classes.colDelete}>
                Delete
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.poUploadCustomerSettings?.edges?.map((edge, index) => (
              <TableRow className={classes.rowBody} key={index}>
                <TableCell>
                  <MyTypography variant="body3">
                    {`${edge?.node?.soldTo?.soldToCode} ${edge?.node?.soldTo?.soldToName}`}
                  </MyTypography>
                </TableCell>
                <TableCell className={classes.colSwitch}>
                  <UISwitch
                    onChange={e => handleToggleSwitch(edge?.node?.soldTo.id, e)}
                    name={index.toString()}
                    defaultChecked={edge?.node?.useCustomerMaster}
                  />
                </TableCell>
                <TableCell className={classes.colDelete}>
                  <IconButton
                    variant="secondary"
                    color="primary"
                    data-testid="delete-customer-button"
                    onClick={() => {
                      setModalMode(ModalMode.DELETE);
                      setSelectedSoldToId(edge?.node?.soldTo.id);
                    }}
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <DialogConfirmDelete
        open={modalMode === ModalMode.DELETE}
        onConfirm={() => handleDelete(selectedSoldToId)}
        onCancel={() => setModalMode(null)}
        title={"ลบรายการที่เลือก!"}
        content={"คุณกำลังจะลบรายการที่เลือก"}
      />
    </Box>
  );
};

export default POSettingsTable;
